import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { IndexRoute, Route, Router } from 'react-router'
import { applyMiddleware, createStore } from 'redux'
import thunk from 'redux-thunk'
import { Provider } from 'react-redux'
import { map, values } from 'lodash'
import { createLogger } from 'redux-logger'

import { withStaticProps } from 'lib/decorators'
import * as blUrls from 'lib/urls'
import { getHistory } from 'lib/history'
import * as BLConstants from 'shared/constants'

import { getTopicSlug, isTopicPage, updateDOMMeta } from '../lib'
import { Ex3Context } from '../lib/contexts'
import helloBabyReducer, { getPostBySlug } from '../reducers'
import { SET_COLLECTIONS, SET_META, SET_POSTS, fetchPosts } from '../actions'
import App from '../components/app'
import LandingPage from './landing-page'
import PostWrapper from './post-wrapper'
import PostsDataLoader from './posts-data-loader'
import PostList from './post-list'
import PostTeaserList from 'hello_baby/components/post-teaser-list'
import TopicView from 'hello_baby/components/topic-view'
import {
  GEAR_SLUG,
  PARENTING_SLUG,
  PREGNANCY_SLUG,
  PREGNANCY_WEEK_BY_WEEK_SLUG,
  SAMPLE_REGISTRIES_SLUG,
  FRIENDS_AND_FAMILY_SLUG,
} from '../constants'
import AdminTool from '../components/admin-tool'
import { withContextualizedTracking, track } from 'lib/analytics'

let middlewares = [thunk]

if (process.env.NODE_ENV === 'development') {
  // middlewares.push(createLogger())
}

const createStoreWithMiddleware = applyMiddleware(...middlewares)(createStore)

const scrollTo = (elId) => {
  let el = document.getElementById(elId)
  if (el) {
    document.body.scrollTop = el.offsetTop - 80 // 80px to account for sticky nav
  }
}

class HelloBabyApp extends Component {
  static propTypes = {
    isAdmin: PropTypes.bool.isRequired,
    meta: PropTypes.object.isRequired,
    path: PropTypes.string.isRequired,
    posts: PropTypes.object.isRequired,
    searchPosts: PropTypes.array.isRequired,
    emailUrls: PropTypes.object.isRequired,
    topics: PropTypes.object.isRequired,
  }

  constructor(props) {
    super(props)

    this.state = { isAdmin: false }

    this.store = createStoreWithMiddleware(helloBabyReducer)
    this.store.dispatch({
      type: SET_COLLECTIONS,
      collections: props.collections,
    })
    this.store.dispatch({ type: SET_META, meta: props.meta })
    this.store.dispatch({ type: SET_POSTS, posts: props.posts })
    this.history = getHistory({ path: props.path, useRouter: true })

    const posts = this.store.getState().posts
    const topicNavigation =
      props.collections[BLConstants.COLLECTION_SLUG_TOPIC_NAVIGATION]

    this.DecoratedLandingPage = withStaticProps('LandingPage', {
      isEmailSubscriber: props.isEmailSubscriber,
      topicNavigation: topicNavigation,
    })(LandingPage)
    this.DecoratedApp = withStaticProps('App', {
      posts: posts,
      topicNavigation: topicNavigation,
      state: this.store.getState(),
    })(App)
    this.DecoratedPostList = withStaticProps('PostList', {
      topicNavigation: topicNavigation,
      emailUrls: props.emailUrls,
    })(PostList)
    this.DecoratedPost = withStaticProps('PostWrapper', {
      emailUrls: props.emailUrls,
    })(PostWrapper)
  }

  static childContextTypes = {
    minimalView: PropTypes.bool,
  }

  getChildContext() {
    return this.props.context
  }

  componentDidMount() {
    this.updateGAMetrics(this.props.path.split('/').pop())
    this.scrollToArticleInPathVariable()
    if (window.currentUser?.admin) {
      this.setState({ isAdmin: true })
    }
  }

  scrollToArticleInPathVariable = () => {
    if (this.props.sectionId) {
      scrollTo(this.props.sectionId)
    }
  }

  render() {
    let searchResults = values(this.props.searchPosts)
    let posts

    return (
      <Provider store={this.store}>
        <Ex3Context.Provider value={this.props.ex3ExperimentGroupName}>
          <>
            {this.state.isAdmin && <AdminTool />}
            <Router history={this.history}>
              <Route
                path={blUrls.helloBabyPath}
                component={this.DecoratedApp}
                onChange={this.handleRouteChange.bind(this)}
              >
                <IndexRoute component={this.DecoratedLandingPage} />
                <Route
                  component={(routerProps) => (
                    <PostsDataLoader shouldFetchPosts={true}>
                      <PostList
                        {...routerProps}
                        topicNavigation={
                          this.props.collections[
                            BLConstants.COLLECTION_SLUG_TOPIC_NAVIGATION
                          ]
                        }
                        emailUrls={this.props.emailUrls}
                        title={`'${routerProps.location.query.search_term}'`}
                        description={`${searchResults.length} results`}
                      >
                        <PostTeaserList posts={searchResults} />
                      </PostList>
                    </PostsDataLoader>
                  )}
                  path={blUrls.helloBabyPostPath('search')}
                />
                <Route path="/" component={this.DecoratedPostList}>
                  <Route
                    component={() => <TopicView slug={GEAR_SLUG} />}
                    path={blUrls.helloBabyPostPath(GEAR_SLUG)}
                  />
                  <Route
                    component={() => (
                      <TopicView
                        slug={PREGNANCY_SLUG}
                        emailUrls={this.props.emailUrls}
                      />
                    )}
                    path={blUrls.helloBabyPostPath(PREGNANCY_SLUG)}
                    params={{ topicSlug: PREGNANCY_SLUG }}
                  />
                  <Route
                    component={() => <TopicView slug={PARENTING_SLUG} />}
                    path={blUrls.helloBabyPostPath(PARENTING_SLUG)}
                    params={{ topicSlug: PARENTING_SLUG }}
                  />
                  <Route
                    component={() => (
                      <TopicView slug={SAMPLE_REGISTRIES_SLUG} />
                    )}
                    path={blUrls.helloBabyPostPath(SAMPLE_REGISTRIES_SLUG)}
                    params={{ topicSlug: SAMPLE_REGISTRIES_SLUG }}
                  />
                  <Route
                    component={() => (
                      <TopicView slug={PREGNANCY_WEEK_BY_WEEK_SLUG} />
                    )}
                    path={blUrls.helloBabyPostPath(PREGNANCY_WEEK_BY_WEEK_SLUG)}
                  />
                  <Route
                    component={() => (
                      <TopicView slug={FRIENDS_AND_FAMILY_SLUG} />
                    )}
                    path={blUrls.helloBabyPostPath(FRIENDS_AND_FAMILY_SLUG)}
                    params={{ topicSlug: FRIENDS_AND_FAMILY_SLUG }}
                  />
                  {map(this.props.topics, (topic) => {
                    return (
                      <Route
                        key={topic.slug}
                        component={() => <TopicView slug={topic.slug} />}
                        path={blUrls.helloBabyPostPath(topic.slug)}
                      />
                    )
                  })}
                </Route>
                <Route
                  path={`${blUrls.helloBabyPath}/:slug`}
                  component={this.DecoratedPost}
                />
                {/* used by iOS app for feed guides */}
                <Route
                  path={`${blUrls.helloBabyPath}/embedded/:slug`}
                  component={this.DecoratedPost}
                />
              </Route>
            </Router>
          </>
        </Ex3Context.Provider>
      </Provider>
    )
  }

  handleRouteChange(prevState, nextState) {
    if (nextState.location.pathname !== prevState.location.pathname) {
      let {
        params: { slug },
      } = nextState
      this.updateMetaData(slug, nextState.location.pathname)
      this.updateGAMetrics(slug, nextState.location.pathname)
      // if the mobile nav menu is open, hide it
      PubSub.publish(BLConstants.HIDE_MOBILE_NAV_MODAL_EVENT)
      // Scroll to the top of the page
      window.scrollTo(0, 0)
    }
  }

  updateMetaData(slug, pathname) {
    let state = this.store.getState()
    let newMeta
    if (slug) {
      newMeta = getPostBySlug(slug, state.posts).meta
    } else {
      let topicSlug = pathname.split('/').pop()
      if (isTopicPage(topicSlug, state)) {
        newMeta = state.meta[topicSlug]
      } else {
        newMeta = state.meta.index
      }
    }
    updateDOMMeta(newMeta.head)
  }

  updateGAMetrics(slug, pathname) {
    let type = null
    let publishedAtYYYYMM = null

    if (slug) {
      let post = getPostBySlug(slug, this.store.getState().posts)
      if (post && post.publishedAt) {
        publishedAtYYYYMM = post.publishedAt.slice(0, 7)
        type = getTopicSlug(post) || post.type
      }
    } else {
      type = pathname.split('/').pop()
    }

    // Content groups must be set before the pageview event is sent.
    // The values persist across router navigation unless explicity 'unset' by setting to null
    ga('set', 'contentGroup1', type) // GA content grouping 'Hello Baby Topics'
    ga('set', 'contentGroup2', publishedAtYYYYMM) // GA content grouping 'Hello Baby Publish Date'
    ga('set', 'contentGroup3', 'Hello Baby') // GA content grouping 'Content Group'
    ga('send', 'pageview', pathname) // send pageview event
  }
}

export default withContextualizedTracking({
  eventLocation: track.EventLocation.GUIDE,
})(HelloBabyApp)
